// src/pages/GeneratedProof.jsx
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { AuthContext } from '../../helpers/AuthContext';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  Container,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
  fetchClientProof,
  fetchClientProofById,
  setClientProofApproval,
} from '../../services/purchaseOrder';
import ProofHeader from './Header';
import styles from './index.module.css';
import { getProofDisplayFileName, getProofReviewTitle } from '../proofs/proofRowUtils';

import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

/* ----------------------------- constants ----------------------------- */

const PREVIEW_LOCK_W = 760; // px — left column max/content width on md+
const PREVIEW_H = { xs: 260, sm: 340, md: 420 }; // responsive preview height
const EMAIL_LS_KEY = 'ec_proof_email';

/* ----------------------------- helpers ----------------------------- */

const safe = (v) => v ?? '';

const isBadId = (id) =>
  id == null ||
  String(id).trim() === '' ||
  String(id).toLowerCase() === 'undefined';

const isPdfUrl = (s = '') => /\.pdf(?:$|[?#])/i.test(s);
const isSvgUrl = (s = '') => /\.svg(?:$|[?#])/i.test(s);
const isImageUrl = (s = '') => /\.(?:png|jpe?g|gif|webp|bmp|tiff?|svg)(?:$|[?#])/i.test(s);
const isVectorDocUrl = (s = '') => /\.(?:ai|eps)(?:$|[?#])/i.test(s);

const coerceBool = (v) => v === true || v === 'true' || v === 1;

const isValidEmail = (s = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());
const getStoredEmail = () => {
  try { return localStorage.getItem(EMAIL_LS_KEY) || ''; } catch { return ''; }
};
const setStoredEmail = (email) => {
  try { if (isValidEmail(email)) localStorage.setItem(EMAIL_LS_KEY, email.trim()); } catch {}
};

const extFromUrl = (url = '') => {
  try {
    const u = new URL(url);
    const m = (u.pathname || '').toLowerCase().match(/\.([a-z0-9]+)$/i);
    return m ? m[1] : '';
  } catch {
    const m = String(url).toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/i);
    return m ? m[1] : '';
  }
};
const isImageExt = (ext = '') => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
const isPdfExt = (ext = '') => ext === 'pdf';

const StatusChip = ({ status }) => {
  const s = (status || '').toLowerCase();
  const color = s === 'approved' ? 'success' : s === 'rejected' ? 'error' : 'default';
  return <Chip label={status || 'Pending'} color={color} size="small" />;
};

// Normalize the single-proof API response into a consistent shape
const normalizeSingle = (resp) => {
  if (!resp) return null;
  const { po, product, decoration } = resp || {};
  const raw = resp.proof || resp || {};
  const id = raw?.id ?? raw?.proofId ?? null;
  const filePath = raw?.filePath ?? raw?.proofFilePath ?? null;
  const hasFile = !!filePath;

  const statusRaw = (raw?.status || '').trim();
  const status = hasFile ? statusRaw : 'Proof Needed';

  return {
    po: po || null,
    product: product || null,
    decoration: decoration || null,
    proof: {
      ...raw,
      id: String(id ?? ''),
      filePath,
      status,
      allProofsUploaded: coerceBool(raw?.allProofsUploaded),
      approvals: raw?.approvals || { user: { state: 'pending' }, client: { state: 'pending' } },
    },
  };
};

/* ------------------------- PDF thumbnail (cached) ------------------------ */

const __pdfThumbCache = new Map();
const PdfThumb = ({ src, width = 720, height = 420 }) => {
  const [dataUrl, setDataUrl] = React.useState(() => __pdfThumbCache.get(src) || '');
  const [fail, setFail] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!src) return;
    if (__pdfThumbCache.has(src)) { setDataUrl(__pdfThumbCache.get(src)); return; }

    (async () => {
      try {
        const task = getDocument({ url: src, withCredentials: false });
        const pdf = await task.promise;
        const page = await pdf.getPage(1);
        const v0 = page.getViewport({ scale: 1 });
        const scale = Math.min(width / v0.width, height / v0.height);
        const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        const url = canvas.toDataURL('image/png');
        __pdfThumbCache.set(src, url);
        setDataUrl(url);
      } catch {
        if (!cancelled) setFail(true);
      }
    })();

    return () => { cancelled = true; };
  }, [src, width, height]);

  if (fail) return null;
  if (!dataUrl) {
    return (
      <div style={{ width, height, display: 'grid', placeItems: 'center', fontSize: 12, color: '#888' }}>
        …
      </div>
    );
  }
  return (
    <img
      src={dataUrl}
      alt="PDF"
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
};

/* ------------------------------ PDF viewer ------------------------------ */

const PdfViewerDialog = ({ open, src, fileName, onClose }) => {
  const canvasRef = React.useRef(null);
  const [numPages, setNumPages] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [scale, setScale] = React.useState(1);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const renderPage = async (pdf, pageNum, scl) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setBusy(true);
    try {
      const p = await pdf.getPage(pageNum);
      const viewport = p.getViewport({ scale: scl });
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d', { alpha: false });
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      await p.render({ canvasContext: ctx, viewport }).promise;
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    (async () => {
      try {
        setErr('');
        const task = getDocument({ url: src, withCredentials: false });
        const pdf = await task.promise;
        if (cancelled) return;
        setNumPages(pdf.numPages);
        setPage(1);
        await renderPage(pdf, 1, scale);
      } catch {
        if (!cancelled) setErr('Could not load PDF.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, src]);

  React.useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    (async () => {
      try {
        const task = getDocument({ url: src, withCredentials: false });
        const pdf = await task.promise;
        if (cancelled) return;
        if (page < 1 || page > pdf.numPages) return;
        await renderPage(pdf, page, scale);
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, scale]);

  const zoomIn  = () => setScale((s) => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.25));
  const prev    = () => setPage((p) => Math.max(p - 1, 1));
  const next    = () => setPage((p) => Math.min(p + 1, numPages));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {fileName || 'Preview'}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={zoomOut} size="small" disabled={busy} title="Zoom out"><ZoomOutIcon fontSize="small" /></IconButton>
          <IconButton onClick={zoomIn}  size="small" disabled={busy} title="Zoom in"><ZoomInIcon fontSize="small" /></IconButton>
          <IconButton onClick={prev}    size="small" disabled={busy || page<=1} title="Previous"><NavigateBeforeIcon fontSize="small" /></IconButton>
          <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>{page} / {numPages || '—'}</Typography>
          <IconButton onClick={next}    size="small" disabled={busy || page>=numPages} title="Next"><NavigateNextIcon fontSize="small" /></IconButton>
          <IconButton size="small" title="Download" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}><FileDownloadOutlinedIcon fontSize="small" /></IconButton>
          <IconButton onClick={onClose} size="small" title="Close"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', placeItems: 'center', bgcolor: '#f7f7f7', minHeight: 420 }}>
        {err ? <Typography color="error">{err}</Typography> : <canvas ref={canvasRef} />}
      </DialogContent>
    </Dialog>
  );
};

/* ----------------- Smart preview (PDF, images, AI/EPS, fallback) ---------------- */

const SmartPreview = ({ filePath, fileName, onOpenPdf }) => {
  const Frame = ({ children }) => (
    <Box
      sx={{
        width: '100%',
        maxWidth: PREVIEW_LOCK_W,
        height: PREVIEW_H,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px dashed #bdbdbd',
        bgcolor: '#fafafa',
        display: 'grid',
        placeItems: 'center',
        boxSizing: "border-box",
        p: 1,
      }}
    >
      {children}
    </Box>
  );

  if (!filePath) {
    return (
      <Frame>
        <Typography>No proof file attached yet.</Typography>
      </Frame>
    );
  }

  if (isPdfUrl(filePath)) {
    const label = fileName || filePath.split('/').pop();
    return (
      <Box sx={{ width: '100%', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          <Frame>
            <PdfThumb src={filePath} width={PREVIEW_LOCK_W - 32} height={420} />
          </Frame>
          <Tooltip title="Open PDF" arrow>
            <IconButton
              size="small"
              aria-label="Preview PDF"
              onClick={onOpenPdf}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              top: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'white',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
              maxWidth: PREVIEW_LOCK_W - 64,
            }}
          >
            <PictureAsPdfOutlinedIcon fontSize="small" />
            <Typography variant="caption" noWrap title={label}>
              {label}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isImageUrl(filePath) || isSvgUrl(filePath)) {
    return (
      <Box sx={{ position: 'relative', width: '100%', maxWidth: PREVIEW_LOCK_W }}>
        <Frame>
          <img
            src={filePath}
            alt={fileName || 'Proof'}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </Frame>
        <Box sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'rgba(255,255,255,0.94)', borderRadius: 1, px: 0.75, py: 0.25, maxWidth: 'calc(100% - 16px)', overflow: 'hidden', boxShadow: 1 }}>
          <Typography variant="caption" noWrap title={fileName}>{fileName || filePath.split('/').pop() || 'Proof'}</Typography>
        </Box>
      </Box>
    );
  }

  if (isVectorDocUrl(filePath)) {
    const label = (fileName || filePath.split('/').pop() || '').toUpperCase();
    return (
      <Frame>
        <Box sx={{ display: 'grid', gap: 1, placeItems: 'center' }}>
          <InsertDriveFileOutlinedIcon />
          <Typography variant="body2" fontWeight={700}>{label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Preview not supported for .ai/.eps. Download to view in Illustrator or compatible software.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            component="a"
            href={filePath}
            download={fileName || undefined}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </Box>
      </Frame>
    );
  }

  const fallbackName = fileName || filePath.split('/').pop();
  return (
    <Frame>
      <Box sx={{ display: 'grid', gap: 1, placeItems: 'center' }}>
        <InsertDriveFileOutlinedIcon />
        <Typography variant="body2" fontWeight={700} noWrap title={fallbackName}>
          {fallbackName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          This file type doesn’t have an in-browser preview. You can still download it.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          component="a"
          href={filePath}
          download={fallbackName || undefined}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none' }}
        >
          Download
        </Button>
      </Box>
    </Frame>
  );
};

/* =============================== MAIN =================================== */

export default function GeneratedProof() {
  const { poId, proofId } = useParams();
  const activeProofId = proofId;

  // Auth (shape): { email, first_name, role, id, status }
  const { authState } = useContext(AuthContext) || {};
  const isInternal    = authState?.status === true || (authState?.role && authState.role !== 'unknown');
  const internalUserId = Number.isFinite(Number(authState?.id)) ? Number(authState.id) : null;
  const internalEmail  = authState?.email || '';
  const internalName   = authState?.first_name || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [po, setPo] = useState(null);
  const [product, setProduct] = useState(null);
  const [decoration, setDecoration] = useState(null);
  const [proof, setProof] = useState(null);

  const [comment, setComment] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  // pdf dialog
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfSrc, setPdfSrc] = useState('');
  const [pdfName, setPdfName] = useState('');

  const openPdf = useCallback((src, name) => {
    setPdfSrc(src);
    setPdfName(name || (src ? src.split('/').pop() : ''));
    setPdfOpen(true);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = poId
        ? await fetchClientProof(poId, activeProofId)
        : await fetchClientProofById(activeProofId);
      const norm = normalizeSingle(resp);
      if (!norm || !norm.proof || isBadId(norm.proof.id)) {
        setPo(null); setProduct(null); setDecoration(null); setProof(null);
        return;
      }
      setPo(norm.po);
      setProduct(norm.product);
      setDecoration(norm.decoration);
      setProof(norm.proof);
      // Seed client email for clients
      if (!isInternal) {
        const seed = getStoredEmail();
        if (seed) setClientEmail((prev) => prev || seed);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load proof.');
    } finally {
      setLoading(false);
    }
  }, [poId, activeProofId, isInternal]);

  useEffect(() => { load(); }, [load]);

  const submitDecision = useCallback(async (state) => {
    try {
      if (!proof || isBadId(proof.id)) {
        toast.error('Invalid proof. Please refresh the page.');
        return;
      }
      setSubmitting(true);

      const cmt = (comment || '').trim();
      if (state === 'rejected' && !cmt) {
        toast.error('Please add a comment before requesting changes.');
        setSubmitting(false);
        return;
      }

      const by =
        isInternal
          ? { name: internalName || 'Internal User', email: internalEmail || '' }
          : { name: 'Client', email: (clientEmail || '').trim() };

      const payload = {
        state,            // 'approved' | 'rejected'
        comments: cmt,
        by,
        actor: isInternal
          ? { type: 'internal', userId: internalUserId ?? null, name: internalName || null, email: internalEmail || null }
          : { type: 'client', email: by.email },
      };

      if (!isInternal && !isValidEmail(payload.actor.email)) {
        toast.error('Please enter a valid email to record this approval.');
        setSubmitting(false);
        return;
      }

      const targetPoId = poId || po?.id;
      if (!targetPoId) {
        toast.error('Missing purchase order id for approval.');
        setSubmitting(false);
        return;
      }

      await setClientProofApproval(targetPoId, String(proof.id), payload);

      if (!isInternal && isValidEmail(by.email)) setStoredEmail(by.email);

      toast.success(state === 'approved' ? 'Approved — thank you!' : 'Sent back for changes.');
      setComment('');
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit your decision.');
    } finally {
      setSubmitting(false);
    }
  }, [poId, po, proof, comment, isInternal, internalUserId, internalName, internalEmail, clientEmail, load]);

  if (loading) {
    return (
      <Box p={6} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!proof) {
    return (
      <Box p={6} textAlign="center">
        <Typography variant="h6">No proof found.</Typography>
      </Box>
    );
  }

  const filePath = proof?.filePath || '';
  const fileName = getProofDisplayFileName(proof);
  const reviewTitle = getProofReviewTitle({ proof, product, decoration });

  const status          = (proof?.status || '').toLowerCase();
  const isFullyApproved = status === 'approved';
  const isRejected      = status === 'rejected';

  const internalState = (proof?.approvals?.user?.state || 'pending').toLowerCase();
  const clientState   = (proof?.approvals?.client?.state || 'pending').toLowerCase();

  const normBy = (by) => {
    if (!by) return { name: '', email: '' };
    if (typeof by === 'string') return { name: by, email: '' };
    return { name: by.name || '', email: by.email || '' };
  };
  const userBy   = normBy(proof?.approvals?.user?.by);
  const clientBy = normBy(proof?.approvals?.client?.by);

  const whenTxt = (at, s) => {
    if (!at) return '—';
    const dt = new Date(at);
    const verb = s === 'approved' ? 'Approved' : s === 'rejected' ? 'Rejected' : 'Updated';
    return `${verb} at ${dt.toLocaleString()}`;
  };

  const initials = (name, fallback) => {
    const base = (name && String(name)) || fallback;
    return base.split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase();
  };

  const canActForRole = isInternal ? (internalState !== 'approved') : (clientState !== 'approved');
  const hasFile = !!filePath;
  const canAct = !isRejected && !isFullyApproved && hasFile && canActForRole;

  return (
    <>
      <ToastContainer />
      <Box className={styles.rootContainer} sx={{ width: '100%' }}>
        <Container maxWidth="lg">
          <ProofHeader
            title="Proof Review"
            meta={[
              `PO ${safe(po?.poNumber)}`,
              product?.Name ? product.Name : null,
              decoration?.Location ? decoration.Location : null
            ].filter(Boolean)}
            rightChips={[proof?.status || 'Pending']}
          />

          <Divider sx={{ mb: 3 }} />

          <Box className={styles.proofContainer}>
            <Grid container spacing={2} className={styles.proofCard}>
              <Grid item xs={12} className={styles.proofGrid}>
                <Grid container spacing={2} sx={{ p: 2 }}>
                  {/* Left column: preview */}
                  <Grid
                    item
                    xs={12}
                    md="auto"
                    sx={{
                      flexBasis: { md: 450 + 24 },
                      maxWidth:  { md: 450 + 24 },
                      mr: { md: 3 },
                      overflow: "hidden"
                    }}
                  >
                    <SmartPreview
                      filePath={filePath}
                      fileName={fileName}
                      onOpenPdf={() => openPdf(filePath, fileName)}
                    />
                  </Grid>

                  {/* Right column: details + actions */}
                  <Grid item xs={12} md sx={{ minWidth: { md: 420 }, flex: '2 1 0' }}>
                    <Box sx={{ position: 'relative' }}>
                      {submitting && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(255,255,255,0.6)',
                            display: 'grid',
                            placeItems: 'center',
                            zIndex: 2,
                          }}
                        >
                          <CircularProgress size={28} />
                        </Box>
                      )}

                      {/* Top line: item + status */}
                      <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1">
                            {reviewTitle}
                          </Typography>
                        </Box>
                        <StatusChip status={proof?.status || 'Pending'} />
                      </Box>

                      {/* Approvers */}
                      <Box mb={1.5}>
                        <Typography variant="subtitle2" gutterBottom>Approvers</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                          {/* Internal */}
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                            <Tooltip
                              arrow
                              title={
                                <Box>
                                  <div><b>{userBy.name || 'Internal Reviewer'}</b></div>
                                  <div style={{opacity:0.8}}>{userBy.email || '—'}</div>
                                  <div style={{opacity:0.8, marginTop: 4}}>
                                    {whenTxt(proof?.approvals?.user?.at, internalState)}
                                  </div>
                                </Box>
                              }
                            >
                              <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#0d47a1' }}>
                                {initials(userBy.name, 'In')}
                              </Avatar>
                            </Tooltip>
                            <Chip
                              size="small"
                              label={`Internal: ${internalState || 'pending'}`}
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>

                          {/* Client */}
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                            <Tooltip
                              arrow
                              title={
                                <Box>
                                  <div><b>{clientBy.name || 'Client Reviewer'}</b></div>
                                  <div style={{opacity:0.8}}>{clientBy.email || '—'}</div>
                                  <div style={{opacity:0.8, marginTop: 4}}>
                                    {whenTxt(proof?.approvals?.client?.at, clientState)}
                                  </div>
                                </Box>
                              }
                            >
                              <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#1b5e20' }}>
                                {initials(clientBy.name, 'Cl')}
                              </Avatar>
                            </Tooltip>
                            <Chip
                              size="small"
                              label={`Client: ${clientState || 'pending'}`}
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Status summary */}
                      <Typography variant="subtitle2" gutterBottom>Current Status</Typography>
                      <Typography variant="body2" gutterBottom>
                        Internal: <b style={{ textTransform: 'capitalize' }}>{internalState}</b> &nbsp;•&nbsp;
                        Client: <b style={{ textTransform: 'capitalize' }}>{clientState}</b>
                      </Typography>

                      {/* Interaction gates */}
                      {isRejected && (
                        <Typography variant="caption" color="error" display="block" mt={2}>
                          This proof has been rejected. Further actions are disabled until manual intervention.
                        </Typography>
                      )}

                      {!isRejected && !isFullyApproved && (
                        <>
                          {isInternal ? (
                            <TextField
                              label="Reviewer email"
                              type="email"
                              fullWidth
                              sx={{ mb: 1.5 }}
                              value={internalEmail || ''}
                              disabled
                            />
                          ) : (
                            <TextField
                              label="Your email (for approval record)"
                              type="email"
                              fullWidth
                              sx={{ mb: 1.5 }}
                              value={clientEmail}
                              onChange={(e) => {
                                const v = e.target.value;
                                setClientEmail(v);
                                if (isValidEmail(v)) setStoredEmail(v);
                              }}
                              disabled={submitting}
                              required
                              error={!!clientEmail && !isValidEmail(clientEmail)}
                              helperText={clientEmail && !isValidEmail(clientEmail) ? 'Enter a valid email (e.g., name@company.com)' : ' '}
                            />
                          )}

                          <TextField
                            label="Comments (optional)"
                            placeholder={hasFile ? 'Tell us what to tweak, or confirm it’s good to go.' : 'No proof attached for this row.'}
                            multiline
                            minRows={3}
                            fullWidth
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            variant="outlined"
                            disabled={submitting || !hasFile || !canAct || (!isInternal && !isValidEmail(clientEmail))}
                            InputProps={{
                              sx: {
                                bgcolor: hasFile ? '#EEE' : '#F5F5F5',
                                borderRadius: 2,
                                '& textarea': {
                                  fontFamily: '"Nunito", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                                },
                              },
                            }}
                          />

                          <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                            <Button
                              variant="contained"
                              onClick={() => submitDecision('approved')}
                              disabled={
                                submitting ||
                                !hasFile ||
                                !canAct ||
                                (!isInternal && !isValidEmail(clientEmail))
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: '#469553',
                                '&:hover': { bgcolor: '#ea580c' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => submitDecision('rejected')}
                              disabled={
                                submitting ||
                                !hasFile ||
                                !canAct ||
                                !comment?.trim() ||
                                (!isInternal && !isValidEmail(clientEmail))
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: '#f97316',
                                '&:hover': { bgcolor: '#ea580c' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                              }}
                            >
                              Request Changes
                            </Button>
                          </Box>
                        </>
                      )}

                      {isFullyApproved && (
                        <Typography variant="caption" color="success.main" display="block" mt={2}>
                          This proof has been fully approved.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box mt={4} textAlign="center" color="text.secondary">
            <Typography variant="caption">
              Need help? Reply to the email you received with this link.
            </Typography>
          </Box>
        </Container>
      </Box>

      {isPdfUrl(pdfSrc) && (
        <PdfViewerDialog open={pdfOpen} src={pdfSrc} fileName={pdfName} onClose={() => setPdfOpen(false)} />
      )}
    </>
  );
}
